/**
 * This module manages the docs model of the API
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 * @author DJ Kim <dj@thebrandingfarm.com>
 */ 
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'

import api from '@/api'
// import router from '@/router'
import { 
    // DOCS
    VUEX_DOCS_GET_REQUEST, 
    VUEX_DOCS_GET_SUCCESS,
    VUEX_DOCS_GET_FAILURE, 
    VUEX_DOCS_APPLY_FILTERS,
    VUEX_DOCS_CLEAR_FILTERS,
    VUEX_DOCS_APPLY_STATUS_FILTER,
    VUEX_DOCS_APPLY_PROGRESS_FILTER,
    VUEX_DOCS_APPLY_TYPE_FILTER,
    VUEX_DOCS_APPLY_DEPARTMENT_FILTER,
    VUEX_DOCS_APPLY_CREATIVE_ROUND_FILTER,
    VUEX_DOCS_SYNC_PAGINATION,
    VUEX_DOCS_SET_APPROVED,
    
    // DOC
    VUEX_DOC_GET_REQUEST, 
    VUEX_DOC_GET_SUCCESS, 
    VUEX_DOC_GET_EVENTS_SUCCESS,
    VUEX_DOC_UPDATE_PROP,
    VUEX_DOC_UPDATE_APPLY_TEMPLATE,
    VUEX_DOC_UPDATE_REQUEST,
    VUEX_DOC_UPDATE_REQUEST_SUCCESS,
    VUEX_DOC_UPDATE_REQUEST_FAILURE,
    
    // CREATE DOC
    VUEX_DOC_CREATE_INIT, 
    VUEX_DOC_CREATE, 
    VUEX_DOC_CREATE_APPLY_TEMPLATE,
    VUEX_DOC_CREATE_SUCCESS,
    VUEX_DOC_CREATE_FAILURE,
    VUEX_DOC_CREATE_UPDATE, 
    VUEX_DOC_CREATE_UPDATE_SUCCESS,
    VUEX_DOC_CREATE_UPDATE_FAILURE,
    VUEX_DOC_CREATE_CANCEL,
    VUEX_DOC_CREATE_UPDATE_PROP,
    VUEX_DOC_CREATE_CANCEL_PROMPT,
    // VUEX_DOC_CREATE_CANCEL_ABORT,
    VUEX_DOC_CREATE_CLEAR_PROPS_TO_UPDATE,
    VUEX_DOC_CREATE_HAS_CHANGED,

    VUEX_DOC_REMOVE,
    VUEX_DOC_REMOVE_SUCCESS,
    VUEX_DOC_REMOVE_FAILURE,

    // ATTACHMENTS
    VUEX_DOC_ATTACH_FILE,
    VUEX_DOC_GET_ATTACHMENTS,
    VUEX_DOC_GET_ATTACHMENTS_SUCCESS,
    VUEX_DOC_REMOVE_FILE,
    VUEX_DOC_REMOVE_FILE_SUCCESS,
    VUEX_DOC_DOWNLOAD_ATTACHMENT } from '@/store/constants/docs'

import { 
    VUEX_ROUTING_EXIT_DOC } from '@/store/constants/routing'

import {
    VUEX_AUTH_LOGOUT } from '@/store/constants/auth'



import {
    VUEX_SNACKBAR_SHOW } from '@/store/constants/snackbar'

/**
 * An empty representation of a doc object model needed for new doc creation
 * @const
 * @todo This will become dynamically registered vuex module when the creation route loads
 */

const createDoc_Template = {
    name:              null,
    used_as:           null,
    created_at:        null,
    updated_at:        null,
    /* files: [
        // FILES
    ] */
}

const updateDoc_Template = {
    name:              null,
    used_as:           null,
    created_at:        null,
    updated_at:        null,
    /* files: [
        // FILES
    ] */
}


/**
* Docs module state
* @const
*/
const state = {
    docs: [],
    docsLinks: {},
    docsLoaded: true,
    docsApproved: true,
    
    doc: {},
    docLinks: {},
    docEvents: {},
    docEventsLinks: {},
    docAttachments: [],
    docLoaded: false,
    restorePreviousDocList: false,
    appliedFilters: [],
    pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true,
        page: 1
    },
    
    updateDoc_Data: {},
    createDoc_Data: {},
    createDoc_PropsToUpdate: {},
    createDoc_Saved: false,
    createDoc_IsSaving: false,
    createDoc_CancelPrompt: false,
    isEdit: false,
}



/**
* Docs module getters
* @const
*/
const getters = {

    /**
    * This getter returns the files attached to the currently loaded doc
    * @function
    * @param {state} state
    */
    getDocAttachments: state => {
        if (state.docLoaded)
            return state.docAttachments
        return []
    },

    /**
    * This getter returns the swaps attached to the currently loaded doc
    * @function
    * @param {state} state
    */
    getDocSwapRows: state => {
        if (state.docLoaded)
            return state.doc.swapRows
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded doc
    * @function
    * @param {state} state
    */
    getDocId: state => {
        if (state.docLoaded)
            return state.doc.id
        return null
    },


    /**
    * This getter returns the swaps attached to the currently loaded doc
    * @function
    * @param {state} state
    */
    getCreateDocData: state => {
        return state.createDoc_Data
    }
}




/**
 * Docs module action definitions
 * @const
 */
const actions = {
    /**
    * Get all docs
    * Params :
        approved: (approved docs | doc requests)
        ...table pagination (sorting, page, itemsPerRow, etc)
        filters
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_DOCS_GET_REQUEST]: async ({ state, commit }) => {
        commit(VUEX_DOCS_GET_REQUEST); 

        // Pagination looks like this 
        // {"rowsPerPage":8,"sortBy":"doc_type_id","page":1,"descending":true,"totalItems":0}
        // Convert it

        let params = Object.assign(
            {
                approved: state.docsApproved,
                page: state.pagination.page,
                per_page: state.pagination.rowsPerPage,
                sort: state.pagination.descending ? '-' + state.pagination.sortBy : state.pagination.sortBy
            },
        )

        for (var f in state.appliedFilters) {
            var key = state.appliedFilters[f]['filterOn']
            var val = state.appliedFilters[f]['id']
            if (!params.hasOwnProperty(key)) {
                params[key] = val
            } else {
                params[key] = params[key] + ',' + val
            }
        }

        await api.get('documents', { params }).then(async (response) => {
            await commit(VUEX_DOCS_GET_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_DOCS_GET_FAILURE, err)
        })  
    },

    [VUEX_DOCS_CLEAR_FILTERS]: async ({ commit }) => {
        await commit(VUEX_DOCS_CLEAR_FILTERS)
    },

    [VUEX_DOCS_APPLY_FILTERS]: async ({ commit, dispatch }, filterItem) => {
        await commit(VUEX_DOCS_APPLY_FILTERS, filterItem)
        await dispatch(VUEX_DOCS_GET_REQUEST)
    },

    [VUEX_DOCS_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'status'
        await dispatch(VUEX_DOCS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_DOCS_APPLY_PROGRESS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'progress'
        await dispatch(VUEX_DOCS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_DOCS_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'type'
        await dispatch(VUEX_DOCS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_DOCS_APPLY_DEPARTMENT_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'department'
        await dispatch(VUEX_DOCS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_DOCS_APPLY_CREATIVE_ROUND_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'cretive_round'
        await dispatch(VUEX_DOCS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_DOCS_SYNC_PAGINATION]: async({commit, dispatch}, pagination) => {
        if (state.docsLoaded) {
            await commit(VUEX_DOCS_SYNC_PAGINATION, pagination)
            await dispatch(VUEX_DOCS_GET_REQUEST)
        }
    },





    /**
    * Get doc -> get events -> get files
    * Params :
        doc id
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_DOC_GET_REQUEST]: async ({ commit }, id) => {
        commit(VUEX_DOC_GET_REQUEST);


        await api.get(`documents/${id}`).then((response) => {
            commit(VUEX_DOC_GET_SUCCESS, response.data)
        })
    },

    /**
    * Update the current doc's properties
    * @function
    */
    [VUEX_DOC_UPDATE_REQUEST]: async ({ state, dispatch, commit }, payload) => {

        await api.patch(`/documents/${state.doc.id}`, payload).then((response) => {
            commit(VUEX_DOC_UPDATE_REQUEST_SUCCESS, response)
            dispatch(VUEX_DOC_GET_REQUEST, state.doc.id)
            dispatch(VUEX_DOCS_GET_REQUEST)
            commit(VUEX_DOC_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your doc has been saved.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })

        }).catch((err) => {
            commit(VUEX_DOC_UPDATE_REQUEST_FAILURE, err)
        })
    },

    /**
     * Update the current doc's name
     * @function
     */
    [VUEX_DOC_UPDATE_PROP]: async ({dispatch}, params) => {
        await dispatch(VUEX_DOC_UPDATE_REQUEST, params)
    },

    /**
     * Create Doc Request
     * @function
     */
    [VUEX_DOC_CREATE_INIT]: ({ state, commit, dispatch }, opts) => {
        commit(VUEX_DOC_CREATE_APPLY_TEMPLATE, opts)
        commit(VUEX_DOC_CREATE_INIT)

        //GLOBAL ACTION PROMPT: Pass array of optional states to watch
        var stateChangeWatchers = [
            ['docs','createDoc_Data']
        ]
        dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', { stateChangeWatchers: stateChangeWatchers})
    },

    [VUEX_DOC_CREATE]: debounce(async ({ state, commit, dispatch }, payload) => {
        commit(VUEX_DOC_CREATE)

        await api.post(`/documents`, payload).then((response) => {
            commit(VUEX_DOC_CREATE_SUCCESS, response.data.data)
            commit(VUEX_DOC_CREATE_CLEAR_PROPS_TO_UPDATE)
            dispatch(VUEX_DOCS_GET_REQUEST)
            commit(VUEX_DOC_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your doc has been created.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })

        }).catch((err) => {
            commit(VUEX_DOC_CREATE_FAILURE)
            console.error(err)
        })      

        // TODO: Move this to dispatch in routing
        // TODO: Diff destination based on role
        // router.push({ name: 'docs-manage' })
    }, 1200),

    [VUEX_DOC_CREATE_UPDATE]: async ({ state, commit, dispatch }) => {
        // commit(VUEX_DOC_CREATE_UPDATE)

        await api.patch(`/documents/${state.createDoc_Data.id}`, state.createDoc_Data).then((response) => {
            commit(VUEX_DOC_CREATE_UPDATE_SUCCESS, response.data.data)
            dispatch(VUEX_DOCS_GET_REQUEST)
            commit(VUEX_DOC_CREATE_CLEAR_PROPS_TO_UPDATE)
        }).catch((err) => {
            commit(VUEX_DOC_CREATE_UPDATE_FAILURE)
            console.error(err)
        })      
    },

    [VUEX_DOC_CREATE_HAS_CHANGED]: ({state, commit}) => {
        if(!isEmpty(state.createDoc_PropsToUpdate)) {
            commit(VUEX_DOC_CREATE_HAS_CHANGED, true)
        } else {
            commit(VUEX_DOC_CREATE_HAS_CHANGED, false)
        }
    },

    [VUEX_DOC_CREATE_CANCEL]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_DOC_CREATE_CANCEL_PROMPT, false)
        commit(VUEX_DOC_CREATE_CANCEL)
    },

    [VUEX_DOC_CREATE_UPDATE_PROP]: ({ commit, dispatch }, payload) => {
        commit(VUEX_DOC_CREATE_UPDATE_PROP, payload)
    },

    [VUEX_DOC_REMOVE]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_DOC_REMOVE)

        await api.delete(`/documents/${state.doc.id}`).then((response) => {
            commit(VUEX_DOC_REMOVE_SUCCESS, response.data.data)
            dispatch(VUEX_DOCS_GET_REQUEST)
            commit(VUEX_DOC_CREATE_CANCEL)
        }).catch((err) => {
            commit(VUEX_DOC_REMOVE_FAILURE)
            console.error(err)
        })  
    },



    /**
	 * File Attachment
	 */
	[VUEX_DOC_ATTACH_FILE]: async ({ dispatch }, payload) => {
		api.post(`/attachments`, {
			file_id: payload.id,
			model_id: payload.attach_to.model_id,
			model: payload.attach_to.model,
			note: payload.note || null,
		}).then(async (response) => {
            // Do we need to get memo attachments immedeiatly after upload?
			// dispatch(VUEX_MEMO_GET_ATTACHMENTS, payload.attach_to.model_id)
		}).catch((err) => {
			console.error(err)
		})
    },
    
    [VUEX_DOC_GET_ATTACHMENTS]: async ({ state, commit }, payload) => {
        let doc_id = payload ? payload : state.doc.id
       
        return api.get(`/documents/${doc_id}/attachments`).then((response) => {
            commit(VUEX_DOC_GET_ATTACHMENTS_SUCCESS, response.data.data)
            return response
        })
    },
    
    [VUEX_DOC_REMOVE_FILE]: async ({ dispatch, commit }, payload) => {
		await dispatch(VUEX_ATTACHMENTS_DELETE_ATTACHMENT, payload).then((response) => {
			if (!response.status) return
			if (response.status == 200 || response.status == 204) 
				commit(VUEX_DOC_REMOVE_FILE_SUCCESS, payload)
		})
    },
    
    [VUEX_DOC_DOWNLOAD_ATTACHMENT]: async ({ dispatch }, payload) => {
		return await dispatch(VUEX_DOC_GET_ATTACHMENTS, payload).then((response) => {
			if (!response.status) return
			if (response.status == 200 || response.status == 204) 
                return response.data.data[0]
		})
	},
}

/**
 * Docs module mutation definitions
 * @const
 */
const mutations = {

    [VUEX_DOCS_GET_REQUEST]: (state) => {
        state.docs = []
        state.docsLoaded = false
        state.pagination.totalItems = 0
    },

    [VUEX_DOCS_GET_SUCCESS]: (state, payload) => {
        state.docs = payload.data
        state.docsLinks = payload.links
        state.docsLoaded = true
        state.pagination.totalItems = payload.meta.total
        state.pagination.page = payload.meta.current_page
    },

    [VUEX_DOCS_GET_FAILURE]: () => {

    },

    //
    // Filtering
    //
    [VUEX_DOCS_APPLY_FILTERS]: (state, filterItem) => {
        let idx = state.appliedFilters.findIndex(x => x.id === filterItem.id && x.filterOn === filterItem.filterOn)
        if (idx > -1) {
            state.appliedFilters.splice(idx, 1) // remove
        } else {
            state.appliedFilters.push(filterItem) // add
        }
    },

    [VUEX_DOCS_CLEAR_FILTERS]: (state) => {
        state.appliedFilters = []
    },

    [VUEX_DOCS_SYNC_PAGINATION]: (state, pagination) => {
        state.pagination = pagination
    },

    [VUEX_DOCS_SET_APPROVED]: (state, value) => {
        state.docsApproved = value
    },
    
    [VUEX_DOC_GET_REQUEST]: (state) => {
        state.doc = {}
        state.docLoaded = false
    },

    [VUEX_DOC_GET_SUCCESS]: (state, payload) => {
        state.doc = payload.data
        state.docLinks = payload.links
        state.docLoaded = true
        state.createDoc_Saved = true
    },

    [VUEX_DOC_GET_EVENTS_SUCCESS]: (state, payload) => {
        state.docEvents = payload.data
        state.docEventsLinks = payload.links
    },

    [VUEX_DOC_UPDATE_APPLY_TEMPLATE]: (state) => {
        Object.assign(state.updateDoc_Data, updateDoc_Template)
    },

    [VUEX_DOC_UPDATE_APPLY_TEMPLATE]: (state) => {
        state.updateDoc_Data = updateDoc_Template
    },


    [VUEX_DOC_UPDATE_REQUEST_SUCCESS]: (rootState, payload) => {
        rootState.lastSuccess = payload
    },

    [VUEX_DOC_UPDATE_REQUEST_FAILURE]: (rootState, error) => {
        rootState.lastError = error
    },

    
    [VUEX_DOC_CREATE_APPLY_TEMPLATE]: (state, opts) => {
        /*if (Object.keys(state.createDoc_Data).length === 0 &&
            state.createDoc_Data.constructor === Object) */
            Object.assign(state.createDoc_Data, createDoc_Template)
    },

    [VUEX_DOC_CREATE_APPLY_TEMPLATE]: (state, opts) => {
        state.createDoc_Data = cloneDeep(createDoc_Template)

        if (opts && opts.edit) {
            state.isEdit = true
        } else {
            state.isEdit = false
        }
    },

    [VUEX_DOC_CREATE_INIT]: (state, payload) => {
        state.createDoc_CancelPromp = false
    },

    // CREATE DOC
    [VUEX_DOC_CREATE]: (state) => {
        state.createDoc_IsSaving = true
    },

    [VUEX_DOC_CREATE_SUCCESS]: (state, payload) => {
        state.createDoc_Data = payload
        state.createDoc_IsSaving = false
        state.createDoc_Saved = true
    },

    [VUEX_DOC_CREATE_FAILURE]: (state) => {
        state.createDoc_IsSaving = false
        state.createDoc_Saved = false
        // TODO: report error
    },


    // UPDATE DOC
    [VUEX_DOC_CREATE_UPDATE]: (state) => {
        state.createDoc_IsSaving = true
    },

    [VUEX_DOC_CREATE_UPDATE_SUCCESS]: (state, payload) => {
        state.createDoc_Data = payload
        state.createDoc_IsSaving = false
    },

    [VUEX_DOC_CREATE_UPDATE_FAILURE]: (state) => {
        state.createDoc_IsSaving = false
        state.createDoc_Saved = false
        // TODO: report error
    },

    [VUEX_DOC_CREATE_CLEAR_PROPS_TO_UPDATE]: (state) => {
        var props = Object.getOwnPropertyNames(state.createDoc_PropsToUpdate);
        for (var i = 0; i < props.length; i++) {
          delete state.createDoc_PropsToUpdate[props[i]]
        }
    },

    //  UPDATE PROP IN STORE
    [VUEX_DOC_CREATE_UPDATE_PROP]: (state, payload) => {
        let mergedData = Object.assign(state.createDoc_Data, payload)
        state.createDoc_Data = cloneDeep(mergedData)

        Object.assign(state.createDoc_PropsToUpdate, payload)
    },

    [VUEX_DOC_CREATE_HAS_CHANGED]: (state, payload) => {
        state.createDoc_HasChanged = payload
    },


    // CANCEL DOC
    [VUEX_DOC_CREATE_CANCEL]: (state) => {
        state.createDoc_IsSaving = false
        //state.createDoc_Saved = false
    },

    [VUEX_DOC_CREATE_CANCEL_PROMPT]: (state, payload) => {
        state.createDoc_CancelPrompt = payload
    },

    [VUEX_DOC_REMOVE]: (state, payload) => {
    },


    [VUEX_DOC_REMOVE_SUCCESS]: (state, payload) => {
        state.createDoc_Data = payload
        state.createDoc_IsSaving = false
    },

    [VUEX_DOC_REMOVE_FAILURE]: (state, payload) => {
        state.createDoc_IsSaving = false
        state.createDoc_Saved = false
        // TODO: report error
    },


    //
    // AUTH EVENTS
    //
    [VUEX_AUTH_LOGOUT]: (state) => {
        state.docs = []
        state.docsLoaded = false
        state.doc = {}
        state.docLoaded = false
    },


    //
    // ROUTING EVENTS
    //
    [VUEX_ROUTING_EXIT_DOC]: (state) => {
        state.restorePreviousDocList = true
        state.pagination.page = 1
        state.pagination.sortBy = 'created_at'
        state.pagination.descending = true
    },


    // DOC ATTACHMENTS
    [VUEX_DOC_GET_ATTACHMENTS_SUCCESS]: (state, payload) => {
        state.docAttachments = payload
    },

    [VUEX_DOC_REMOVE_FILE_SUCCESS]: (state, payload) => {
        let fileIndex = state.docAttachments.findIndex(file => file.file_id === payload.file_id)
		if (fileIndex > -1) {
			state.docAttachments.splice(fileIndex, 1)
		}
    }

}

/**
 * The Docs module of our Vuex store.
 * @module store/modules/docs
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
